import { useAuth } from '@/components/auth/AuthProvider';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import logoIcon from '@/assets/smb-logo-icon.svg';

interface TeamLogoProps {
  size?: string;
  className?: string;
  onTeamLogoLoaded?: (hasTeamLogo: boolean) => void;
}

interface TeamData {
  company_logo_url?: string;
  name?: string;
}

export const TeamLogo = ({ size = "w-5 h-5", className = "flex-shrink-0", onTeamLogoLoaded }: TeamLogoProps) => {
  const { currentTeamId } = useAuth();
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (!currentTeamId) {
        setLoading(false);
        onTeamLogoLoaded?.(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('teams')
          .select('company_logo_url, name')
          .eq('id', currentTeamId)
          .single();

        if (error) throw error;
        setTeamData(data);
        // Notify parent if team logo is available
        onTeamLogoLoaded?.(!!data?.company_logo_url);
      } catch (error) {
        console.error('Error fetching team data:', error);
        setTeamData(null);
        onTeamLogoLoaded?.(false);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [currentTeamId, onTeamLogoLoaded]);

  if (loading) {
    return <div className={`${size} ${className} bg-muted animate-pulse rounded`} />;
  }

  // Show team logo if available, otherwise fallback to SMB logo
  if (teamData?.company_logo_url) {
    return (
      <img 
        src={teamData.company_logo_url} 
        alt={`${teamData.name || 'Team'} Logo`} 
        className={`${size} ${className} object-contain`}
        onError={(e) => {
          // Fallback to SMB logo if team logo fails to load
          e.currentTarget.src = logoIcon;
          e.currentTarget.alt = 'SMB Logo';
          onTeamLogoLoaded?.(false);
        }}
      />
    );
  }

  // Default SMB logo fallback
  return (
    <img 
      src={logoIcon} 
      alt="SMB Logo" 
      className={`${size} ${className}`}
    />
  );
};